import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import AthleteLayout from "../common/AthleteLayout";
import { AthleteHeader } from "../common/AthleteHeader";
import { useAthleteAuthContext } from "../login/useAthleteAuthContext";
import API_URL from "../../api/API";

export const AthleteStrengthWorkout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { athlete } = useAthleteAuthContext();
  const { product_id } = useParams();

  // State possibly passed from previous page
  const productFromState = location.state?.product_basic ?? null;
  const restoredOpenIndex = location.state?.openIndex ?? null;

  const [product, setProduct] = useState(productFromState);
  const [openIndex, setOpenIndex] = useState(restoredOpenIndex);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Helper: check if product has usable weeks
  const hasWeeks = (p) => Array.isArray(p?.weeks) && p.weeks.length > 0;

  useEffect(() => {
    let mounted = true;

    // If we already have product with weeks, no need to fetch
    if (productFromState && hasWeeks(productFromState)) {
      setProduct(productFromState);
      setLoading(false);
      return;
    }

    // Otherwise fetch from server
    const fetchProduct = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(
          `${API_URL.FRONTEND_ATHLETE_PURCHASED_STRENGTH_PRODUCT_DETAIL}${athlete.id}/${product_id}`,
          {
            headers: { Authorization: `Bearer ${athlete.token}` },
          },
        );

        const json = await response.json();

        if (!mounted) return;

        if (response.ok && json?.data) {
          setProduct(json.data);
        } else {
          // If backend responded but no data/weeks, still set product so UI can show message
          setProduct(json?.data ?? null);
          const msg = json?.message || "Failed to load product data";
          setError(msg);
          console.error("Fetch product response not OK:", json);
        }
      } catch (err) {
        console.error("Fetch product error:", err);
        if (mounted) {
          setError(err.message || "Network error");
        }
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchProduct();

    return () => {
      mounted = false;
    };
  }, [product_id, athlete?.id, athlete?.token, productFromState]);

  const toggleAccordion = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const openDay = (weekNumber, dayName, items) => {
    // ensure product exists before navigating
    if (!product?._id) return;
    navigate(`/athlete/strength-product/strength-workout/day/${product._id}`, {
      state: {
        product_basic: product,
        weekNumber,
        dayName,
        items,
        openIndex, // preserve open accordion for back navigation
      },
    });
  };

  return (
    <AthleteLayout>
      <div className="h-full flex flex-col bg-white">
        <div className="flex-1 overflow-y-auto">
          {/* LOADING */}
          {loading && (
            <div className="w-full flex justify-center items-center py-10">
              <p className="text-lg font-medium text-grayish">
                Loading strength workout...
              </p>
            </div>
          )}

          {/* ERROR */}
          {!loading && error && (
            <div className="w-full flex justify-center items-center py-10">
              <p className="text-lg font-medium text-red-500">Error: {error}</p>
            </div>
          )}

          {/* CONTENT */}
          {!loading && !error && product && (
            <>
              <div className="pb-3 w-full max-w-2xl mx-auto flex flex-row justify-center items-center gap-10 px-5">
                <p className="w-full py-3 text-black text-xl line-clamp-2 leading-tight text-center border-b border-black/30">
                  {product.title || "Strength Product"}
                </p>
              </div>

              <div className="w-full max-w-2xl mx-auto px-5">
                <div className="py-3">
                  <div className="flex justify-between gap-8 mb-4">
                    <button className="text-black border-b-2 border-yellow-600 text-base pb-2 font-bold">
                      Strength Workout
                    </button>
                  </div>
                </div>
                <div className="space-y-5">
                  {hasWeeks(product) ? (
                    product.weeks.map((week, weekIndex) => (
                      <div
                        key={weekIndex}
                        className={`${
                          openIndex === weekIndex && "!border-black/30"
                        } border border-transparent rounded-xl`}
                      >
                        <button
                          className="w-full flex justify-between items-center gap-5 text-white text-base font-bold bg-black text-left px-4 py-3 rounded-lg"
                          onClick={() => toggleAccordion(weekIndex)}
                        >
                          <span>Week {week.weekNumber}</span>
                          <span className={`transform transition-transform`}>
                            {openIndex === weekIndex ? <Minus /> : <Plus />}
                          </span>
                        </button>

                        {openIndex === weekIndex && (
                          <div className="px-10 py-3">
                            {Object.entries(week.days || {})
                              .filter(
                                ([_, day]) =>
                                  Array.isArray(day?.items) &&
                                  day.items.length > 0,
                              )
                              .map(([dayName, day], dayIndex) => (
                                <div
                                  key={dayIndex}
                                  className="w-fit flex justify-between items-center"
                                >
                                  <button
                                    onClick={() =>
                                      openDay(
                                        week.weekNumber,
                                        dayName,
                                        day.items,
                                      )
                                    }
                                    className="w-full py-1 text-left text-sm font-medium text-black capitalize border-b-2 border-transparent hover:border-darkYellow"
                                  >
                                    {dayName.charAt(0).toUpperCase() +
                                      dayName.slice(1)}{" "}
                                    - {day.training_focus || "Training Focus"}{" "}
                                    {/*({day.items.length} items)*/}
                                  </button>
                                </div>
                              ))}
                          </div>
                        )}
                      </div>
                    ))
                  ) : (
                    <p className="text-center text-black mt-5">
                      No strength training template found.
                    </p>
                  )}
                </div>
              </div>
            </>
          )}

          {/* If nothing returned from backend */}
          {!loading && !error && !product && (
            <div className="w-full flex justify-center items-center py-10">
              <p className="text-lg text-grayish">
                No strength training template found.
              </p>
            </div>
          )}
        </div>
      </div>
    </AthleteLayout>
  );
};

const Plus = () => {
  return (
    <svg
      width="24"
      className="w-full h-full"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      preserveAspectRatio="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M0 0H24V24H0V0Z" fill="#C19E42" />
      <path d="M11 4.5H13V19.5H11V4.5Z" fill="black" />
      <path d="M4.5 13V11H19.5V13H4.5Z" fill="black" />
    </svg>
  );
};

const Minus = () => {
  return (
    <svg
      width="24"
      className="w-full h-full"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      preserveAspectRatio="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M0 0H24V24H0V0Z" fill="#C19E42" />
      {/* <path d="M10.5 4H13.5V20H10.5V4Z" fill="black" /> */}
      <path d="M4.5 13V11H19.5V13H4.5Z" fill="black" />
    </svg>
  );
};
